import { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import { Scissors, Upload, Loader2, Download, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const downloadWithAuth = async (url, filename) => {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("Error al descargar");
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

const CleanUrlsTab = () => {
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);

  const handleClean = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCleaning(true);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/sheets-tool/clean-urls`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        toast.success(`${data.result_count} URLs limpiadas`);
      } else {
        toast.error(data.detail || "Error limpiando URLs");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setCleaning(false);
    e.target.value = "";
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      await downloadWithAuth(
        `${API}/sheets-tool/results/${result.result_id}/export-excel`,
        `urls_limpias_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    } catch (error) {
      toast.error("Error descargando archivo");
    }
  };

  return (
    <div className="space-y-6" data-testid="clean-urls-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Limpiar URLs</h2>
        <p className="text-muted-foreground">Extraer dominio raíz de URLs completas</p>
      </div>

      {/* Info Card */}
      <Card className="bg-red-500/5 border-red-500/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Scissors className="w-8 h-8 text-red-500 shrink-0" />
            <div className="text-sm space-y-2">
              <p className="font-semibold text-red-600 dark:text-red-400">¿Qué hace esta herramienta?</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Extrae el dominio raíz de cualquier URL</li>
                <li>Elimina www, rutas, parámetros y protocolos</li>
                <li>Preserva todas las demás columnas de tu archivo</li>
              </ul>
              <div className="mt-3 p-3 bg-background rounded-lg border">
                <p className="text-xs font-medium mb-2">Ejemplo de transformación:</p>
                <div className="space-y-1">
                  <p className="text-xs font-mono">
                    <span className="text-muted-foreground">https://www.ejemplo.com/pagina?id=123</span>
                  </p>
                  <p className="text-xs font-mono text-green-600 dark:text-green-400">
                    → ejemplo.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-5 h-5 text-red-500" />
            Subir Archivo
          </CardTitle>
          <CardDescription>
            Sube un archivo CSV o Excel con una columna de URLs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            id="clean-upload"
            type="file"
            className="hidden"
            accept=".csv,.xlsx,.xls"
            onChange={handleClean}
          />
          <Button
            className="w-full"
            variant={cleaning ? "secondary" : "default"}
            onClick={() => document.getElementById("clean-upload")?.click()}
            disabled={cleaning}
          >
            {cleaning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Limpiando URLs...
              </>
            ) : (
              <>
                <Scissors className="w-4 h-4 mr-2" />
                Subir y Limpiar URLs
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Result Section */}
      {result && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader>
            <CardTitle className="text-base text-green-600 dark:text-green-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {result.result_count} URLs limpiadas correctamente
            </CardTitle>
            <CardDescription>
              El archivo está listo para descargar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleDownload} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Descargar Excel con URLs Limpias
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Additional Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Formatos soportados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="font-medium">.xlsx</p>
              <p className="text-xs text-muted-foreground">Excel</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="font-medium">.xls</p>
              <p className="text-xs text-muted-foreground">Excel (antiguo)</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="font-medium">.csv</p>
              <p className="text-xs text-muted-foreground">CSV</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            La herramienta detectará automáticamente la columna que contiene las URLs.
            Si hay múltiples columnas con URLs, se procesará la primera encontrada.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CleanUrlsTab;
